/**
 * Build-time configuration, the dashboard's `src/env.ts` on a smaller surface.
 * One tenant and one SPA client cover both apps (modules/auth0/spa.tf), so the
 * domain / client id / audience an admin build is given are the same three
 * strings the dashboard build is given.
 */

export type AuthMode = 'dev' | 'auth0';

const rawMode = import.meta.env.VITE_AUTH_MODE;

export const AUTH_MODE: AuthMode = rawMode === 'auth0' ? 'auth0' : 'dev';

/**
 * local-dev.md §2.4: the dev-auth caller. The default is the seeded operator
 * account, which holds the `admin` permission and no membership row — the shape
 * of a real operator, not an accident of seeding.
 */
export const DEV_USER = import.meta.env.VITE_DEV_USER ?? 'soroush@arbiterdefense.group';

export const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN ?? '';
export const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID ?? '';
/**
 * The same static, opaque identifier the dashboard build is given — not a URL
 * anything serves, and in particular not this app's own origin. Declared once in
 * `deployment/terraform/locals.tf` as `auth0_api_identifier`; see the dashboard's
 * `src/env.ts` for the full note.
 */
export const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE ?? '';

/**
 * The tenant's only connection (`modules/auth0/google.tf`). Naming it skips
 * Auth0's choose-a-method screen and goes straight to Google.
 */
export const AUTH0_CONNECTION = 'ni-google';

/**
 * The one redirect target Terraform registers:
 * `callbacks = [for o in var.spa_origins : "${o}/callback"]` (spa.tf). It is
 * where Auth0 is allowed to send the code back to, and not a screen: the
 * provider exchanges the code and puts the address bar back on the path sign-in
 * started from (auth.tsx). The router carries a `/callback` route anyway, for
 * the reader who reloads that address with a live session and nothing to
 * exchange.
 */
export const CALLBACK_PATH = '/callback';

/**
 * A build that asks for Auth0 without the configuration cannot sign anyone in.
 * Say so at boot rather than redirecting at a tenant that is not there.
 */
if (AUTH_MODE === 'auth0') {
  const missing = [
    AUTH0_DOMAIN ? null : 'VITE_AUTH0_DOMAIN',
    AUTH0_CLIENT_ID ? null : 'VITE_AUTH0_CLIENT_ID',
    AUTH0_AUDIENCE ? null : 'VITE_AUTH0_AUDIENCE',
  ].filter((name): name is string => name !== null);

  if (missing.length > 0) {
    throw new Error(
      `VITE_AUTH_MODE=auth0 but this build is missing ${missing.join(', ')}. ` +
        'Set them at build time, or build with VITE_AUTH_MODE=dev against a local server.',
    );
  }
}
